/**
 * Whether a cloud run loads this repository's own committed skills (YAG-784):
 * `GET/PUT /api/repos/:repoId/agent-trust`. The server says who may change it
 * (`canEdit`: a workspace admin, or the owner of a Team that works on the
 * repository), so the switch follows the server rather than guessing.
 *
 * The switch moves at once and goes back if the write is refused. A response
 * for a repository no longer shown is dropped.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { RepoAgentTrust, RepoAgentTrustUpdateRequest } from "@yagni/shared";
import { AGENT_TRUST_FAILED, AGENT_TRUST_LOAD_FAILED } from "../lib/agentSourcesCopy.js";
import { readError } from "./agentSourcesApi.js";

function trustUrl(repoId: string): string {
  return `/api/repos/${encodeURIComponent(repoId)}/agent-trust`;
}

export function useRepoAgentTrust(repoId: string | null) {
  const [trust, setTrust] = useState<RepoAgentTrust | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(repoId);
  current.current = repoId;

  useEffect(() => {
    setTrust(null);
    setError(null);
    if (!repoId) return;
    let live = true;
    void (async () => {
      try {
        const res = await fetch(trustUrl(repoId), { credentials: "include" });
        if (!res.ok) {
          console.warn(`Agent trust: the read for repository ${repoId} answered ${res.status}.`);
          throw new Error(AGENT_TRUST_LOAD_FAILED);
        }
        const data = (await res.json()) as Partial<RepoAgentTrust> | null;
        if (live) setTrust({ trusted: data?.trusted === true, canEdit: data?.canEdit === true });
      } catch (err) {
        if (!(err instanceof Error && err.message === AGENT_TRUST_LOAD_FAILED)) {
          console.warn(`Agent trust: the read for repository ${repoId} failed.`, err);
        }
        if (live) setError(AGENT_TRUST_LOAD_FAILED);
      }
    })();
    return () => {
      live = false;
    };
  }, [repoId]);

  const setTrusted = useCallback(
    async (trusted: boolean) => {
      if (!repoId || !trust) return;
      const asked = repoId;
      const previous = trust;
      setError(null);
      setBusy(true);
      setTrust({ ...previous, trusted });
      // A non-OK answer is warned with its status where it is read; anything
      // else that lands in the catch (a network failure, a parse error) is
      // warned there, so every failed write leaves exactly one line.
      let warned = false;
      try {
        const res = await fetch(trustUrl(repoId), {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ trusted } satisfies RepoAgentTrustUpdateRequest),
        });
        if (!res.ok) {
          console.warn(`Agent trust: the write for repository ${repoId} answered ${res.status}.`);
          warned = true;
          throw new Error(await readError(res, AGENT_TRUST_FAILED));
        }
        const data = (await res.json().catch(() => null)) as Partial<RepoAgentTrust> | null;
        if (current.current === asked && typeof data?.trusted === "boolean") {
          setTrust({ ...previous, trusted: data.trusted });
        }
      } catch (err) {
        if (!warned) console.warn(`Agent trust: the write for repository ${repoId} failed.`, err);
        if (current.current === asked) {
          setTrust(previous);
          setError(err instanceof Error && err.message ? err.message : AGENT_TRUST_FAILED);
        }
      } finally {
        setBusy(false);
      }
    },
    [repoId, trust],
  );

  return { trust, busy, error, setTrusted };
}
